import { Component, Input, OnInit } from '@angular/core';

import * as _ from 'lodash';
import * as d3 from 'd3';
import * as moment from 'moment';
import { UserService } from '../../../../services/core.user.service';
import { UiService } from '../../../../services/core.ui.service';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { ProjectReportsData } from '../../services/project.reports.data.service';
import { ProjectService } from '../../../../services/project.service';
import { doPromiseAll } from '../../../../config/core.promise-utils';

@Component({
    selector: 'project-summary-report',
    template: require('./project.report.summary.component.html')
})
export class ProjectSummaryReportComponent implements OnInit {

    @Input() project: any = null;

    private emailReportPreferenceName = '';
    private periodReportPreferenceName = '';

    protected topChartOptions: any;

    protected items: any = false;
    protected topCommittersData: any = [];
    protected topUsersData: any = [];
    private showDetail: any = false;
    protected startDate: any = false;
    protected endDate: any = false;

    private reportParams: any = false;

    protected yesNo = {
        'Yes': 1,
        'No': 0
    };
    protected receiveWeeklyEmail = false;

    protected dateRangeOptions = {
        'Daily': 'daily',
        'Weekly': 'weekly',
        'Monthly': 'monthly'
    };
    protected dateRangeChoice = 'weekly';

    // Passthroughs
    protected refreshData: any;
    protected downloadData: any;

    constructor(
        private userService: UserService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
        private projectReportsData: ProjectReportsData,
        private projectService: ProjectService,
    ) { }
    
    ngOnInit() {
        this.topChartOptions = {
            chart: {
                type: 'pieChart',
                height: 360,
                width: 360,
                x: function (d) { return d.key; },
                y: function (d) { return d.y; },
                showLabels: false,
                transitionDuration: 200,
                labelThreshold: 0.01,
                noData: this.gfTranslateService.translate("No Data Available"),
                legendPosition: 'bottom',
                showLegend: false,
                tooltips: true,
                valueFormat: d3.format('d'),
                labelsOutside: true
            }
        };

        this.emailReportPreferenceName = 'proj-sum-email-' + this.project.id;
        this.periodReportPreferenceName = 'proj-sum-period-' + this.project.id;

        this.reportParams = {
            project: this.project.unixName,
            type: 'summary'
        };

        this.refreshData = this.loadReportData;

        this.downloadData = _.partial(
            this.projectReportsData.runReport,
            this.reportParams
        );

        var prefs = {
            email: this.userService.preference.getPreference(this.emailReportPreferenceName, 0),
            dateRange: this.userService.preference.getPreference(this.periodReportPreferenceName, 'weekly')
        };

        doPromiseAll(prefs)
            .then((results) => {
                this.dateRangeChoice = _.get(results, 'dateRange.preferenceValue', 'weekly');
                this.receiveWeeklyEmail = _.get(results, 'email.preferenceValue', 0) != 0;
            })
            .finally(this.loadReportData);
    };

    setDateRangePreference(value) {
        this.dateRangeChoice = value;
        this.userService.preference.setPreference(this.periodReportPreferenceName, this.dateRangeChoice)
            .then(this.loadReportData, this.preferenceSaveFail);
    };

    setEmailPreference(value) {
        this.receiveWeeklyEmail = value;
        this.userService.preference.setPreference(this.emailReportPreferenceName, this.receiveWeeklyEmail)
            .then(() => {}, this.preferenceSaveFail);
    };

    preferenceSaveFail(result) {
        this.uiService.message.showSuccess(
            this.gfTranslateService.translate('Sorry, could not save your preference'),
            this.gfTranslateService.translate('Preference Save')
        );
    }

    setDateRanges() {
        var days = 0;
        switch (this.dateRangeChoice) {
            case 'daily':
                days = 1;
                break;

            case 'monthly':
                days = 30;
                break;

            case 'weekly':
            default:
                days = 7;
                break;
        }

        this.startDate = moment().subtract(days, 'd').format('YYYY-MM-DD');
        this.endDate = moment().format('YYYY-MM-DD');
    }

    loadReportData = () => {

        this.items = false;
        this.topCommittersData = false;
        this.topUsersData = false;

        this.setDateRanges();

        this.projectService.report(this.reportParams)
            .then(
                (response) => {
                    this.items = _.get(response, 'items', {});

                    if (this.items.top_committers) {
                        this.topCommittersData = this.projectReportsData.setPieChartData(
                            this.getCommittersPieChartData(this.items.top_committers)
                        );
                    }

                    if (this.items.top_users) {
                        this.topUsersData = this.projectReportsData.setPieChartData(
                            this.getTopUsersPieChartData(this.items.top_users)
                        );
                    }
                },
                (response) => {
                    console.log(response);
                    var msg = this.gfTranslateService.translate('There was a problem loading project summary data');
                    this.uiService.message.showWarning(msg, 'Data Error');
                }
            );
    }

    getCommittersPieChartData(items) {
        return _.map(
            items,
            function (item) {
                return {
                    source: item.unixName,
                    quantity: item.commit_count
                };
            }
        )
    }

    getTopUsersPieChartData(items) {
        return _.map(
            items,
            function (item) {
                return {
                    source: item.unixName,
                    quantity: item.activity_count
                };
            }
        );
    }
}